import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Container, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';
import { Query, QueryDocumentSnapshot, QuerySnapshot, and, collection, endBefore, getDocs, getFirestore, limit, limitToLast, or, orderBy, query, startAfter, startAt, where } from 'firebase/firestore';
import { PersonalDocument, PersonalDocumentType } from '../../types/PersonalDocument';
import { queryPersonalDocumentsCountExpiresIn } from '../../utils/queryPersonalDocuments';
import personalDocumentConverter from '../../converters/personalDocumentConverter';
import { DateTime } from 'luxon';
import { ArrowForwardIosRounded, InfoRounded } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import { count } from 'console';
import employeeConverter from '../../converters/employeeConverter';

export const NotificationsPersonalDocumentsPage = () => {
  const db = getFirestore();
  const navigate = useNavigate();

  const [personalDocumentType, setPersonalDocumentType] = useState<PersonalDocumentType>('Passport');
  const [personalDocuments, setPersonalDocuments] =  useState<PersonalDocument[]>([]);

  const [month, setMonth] = useState(3);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);

  const [firstRow, setFirsRow] = useState<QueryDocumentSnapshot | null>(null);
  const [lastRow, setLastRow] = useState<QueryDocumentSnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async (qSnapshot: QuerySnapshot<PersonalDocument>) => {
    setFirsRow(qSnapshot.docs[0]);
    setLastRow(qSnapshot.docs[qSnapshot.docs.length - 1]);
    setPersonalDocuments(qSnapshot.docs.map(qDocSnapshot => {
      return qDocSnapshot.data();
    }));

    setLoading(false);
  };

  const handlePageOnChange = (event: React.MouseEvent<HTMLButtonElement> | null, to: number) => {
    const collectionPath = `PersonalDocuments/${personalDocumentType}/List`;
    const dateString = DateTime.utc().plus({ month }).toFormat('yyyyMMdd');

    let q: Query<PersonalDocument> | null = null;
    if (to > page) {
      // Next
      q = query(collection(db, collectionPath), where('expire', '<=', dateString), orderBy('expire', 'asc'), startAfter(lastRow), limit(rowsPerPage)).withConverter(personalDocumentConverter);
    } else if (to < page) {
      // Prev
      q = query(collection(db, collectionPath), where('expire', '<=', dateString), orderBy('expire', 'asc'), endBefore(firstRow), limitToLast(rowsPerPage)).withConverter(personalDocumentConverter);
    } else {
      // Same page
      q = query(collection(db, collectionPath), where('expire', '<=', dateString), orderBy('expire', 'asc'), startAt(firstRow), limit(rowsPerPage)).withConverter(personalDocumentConverter);
    }

    getDocs(q).then((qSnapshot) => {
      refresh(qSnapshot).then(() => {
        setPage(to);
      });
    });
  };

  const handleRowsPerPageOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log(+event.target.value)
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {

    setLoading(true);
    getDocs(
      query(
        collection(db, `PersonalDocuments/${personalDocumentType}/List`),
        where('expire', '<=', DateTime.utc().plus({ month }).toFormat('yyyyMMdd')),
        orderBy('expire', 'asc'),
        limit(rowsPerPage),
      ).withConverter(personalDocumentConverter)
    ).then((qSnapshot) => refresh(qSnapshot));
    queryPersonalDocumentsCountExpiresIn(personalDocumentType).then(count => {
      setTotalRows(count);
    });
  }, [rowsPerPage]);

  return (
    <Container disableGutters>
      <Box mt={2} mb={5}>
        <Typography variant="h5">Personal Documents</Typography>
        <Typography color='GrayText'>Below are documents that will expire in the the next {month} months.</Typography>
      </Box>
      <Paper elevation={4} sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={76} align="right">Count</TableCell>
                <TableCell align="left">Employee</TableCell>
                <TableCell width={180} align="left">Id Number</TableCell>
                <TableCell width={180} align="left">Document</TableCell>
                <TableCell width={300} align="left">Expiration</TableCell>
                <TableCell width={72} align="center"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell colSpan={6}>
                    <Box sx={{ height: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                      <CircularProgress />
                      <Typography variant='caption' sx={{ mt: 2 }}>Please wait...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {personalDocuments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant='caption'>
                      No {personalDocumentType} documents will expire in the next {month} months.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {personalDocuments.map((e, i) => {
                return (
                  <TableRow
                    key={e.idNumber}
                    hover
                    tabIndex={-1}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row" align="right">{(page * rowsPerPage) + i + 1}</TableCell>
                    <TableCell align="left">{e.name}</TableCell>
                    <TableCell align="left">{e.idNumber}</TableCell>
                    <TableCell align="left">{personalDocumentType}</TableCell>
                    <TableCell align="left">{DateTime.fromFormat(e.expire, 'yyyyMMdd').toFormat('MMMM dd, yyyy')}</TableCell>
                    <TableCell>
                      <Tooltip title='View'>
                        <span>
                          <IconButton color='info' onClick={() => navigate(`/home/employees/${e.idNumber}/documents`)}><ArrowForwardIosRounded /></IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[25, 50, 100]}
          colSpan={5}
          count={totalRows}
          page={page}
          rowsPerPage={rowsPerPage}
          SelectProps={{
            inputProps: {
              'aria-label': 'rows per page',
            },
          }}
          onPageChange={handlePageOnChange}
          onRowsPerPageChange={handleRowsPerPageOnChange}
          ActionsComponent={TablePaginationActions} />
      </Paper>
    </Container>
  );
};
